/**
 * Advanced analytics readers (Analytics & Intelligence spec §3/§4) over the
 * 0147 read-model views — MRR & movement, churn/retention/cohort/tenure, freeze,
 * plan performance, members-at-risk / ghost / revenue-at-risk, acquisition
 * channel, class performance, waitlist demand, session-pack burn-down, AR aging.
 * All views are security_invoker (tenant-isolated by RLS); we additionally filter
 * by tenant_id. Money is integer minor units (denari).
 */
import { ApiError } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

async function rows<T>(
  p: PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T[]> {
  const { data, error } = await p;
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as T[];
}

// ── MRR ───────────────────────────────────────────────────────────────────────
export interface MrrSummary {
  mrr: number;
  active_members: number;
  active_recurring: number;
  arpm: number;
}
export async function getMrrSummary(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<MrrSummary | null> {
  const { data, error } = await c
    .from('v_mrr_summary')
    .select('mrr, active_members, active_recurring, arpm')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data as unknown as MrrSummary) ?? null;
}

export interface MrrMovementRow {
  month_bucket: string;
  new_mrr: number;
  expansion_mrr: number;
  contraction_mrr: number;
  churned_mrr: number;
  net_mrr_change: number;
}
export function getMrrMovement(c: GymSupabaseClient, tenantId: string): Promise<MrrMovementRow[]> {
  return rows<MrrMovementRow>(
    c.from('v_mrr_movement').select('*').eq('tenant_id', tenantId).order('month_bucket'),
  );
}

// ── Churn / retention / cohort / tenure / freeze ──────────────────────────────
export interface ChurnRow {
  month_bucket: string;
  new_members: number;
  churned_members: number;
  reactivations: number;
  net_growth: number;
  cumulative_active: number;
  churn_rate_pct: number | null;
}
export function getMembershipChurn(c: GymSupabaseClient, tenantId: string): Promise<ChurnRow[]> {
  return rows<ChurnRow>(
    c.from('v_membership_churn').select('*').eq('tenant_id', tenantId).order('month_bucket'),
  );
}

export interface CohortRow {
  cohort_month: string;
  cohort_size: number;
  retained_active: number;
  retention_pct: number | null;
}
export function getCohortRetention(c: GymSupabaseClient, tenantId: string): Promise<CohortRow[]> {
  return rows<CohortRow>(
    c.from('v_membership_cohort').select('*').eq('tenant_id', tenantId).order('cohort_month'),
  );
}

export interface TenureRow {
  member_id: string;
  tenure_days: number;
  currently_active: boolean;
}
export function getMemberTenure(c: GymSupabaseClient, tenantId: string): Promise<TenureRow[]> {
  return rows<TenureRow>(
    c
      .from('v_member_tenure')
      .select('member_id, tenure_days, currently_active')
      .eq('tenant_id', tenantId),
  );
}

export interface FreezeAnalytics {
  memberships_frozen: number;
  returned_active: number;
  exited: number;
  still_frozen: number;
}
export async function getFreezeAnalytics(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<FreezeAnalytics | null> {
  const { data, error } = await c
    .from('v_freeze_analytics')
    .select('memberships_frozen, returned_active, exited, still_frozen')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data as unknown as FreezeAnalytics) ?? null;
}

// ── Plans ─────────────────────────────────────────────────────────────────────
export interface PlanPerformanceRow {
  plan_id: string;
  plan_name: string;
  plan_type: string;
  price: number;
  units_sold: number;
  revenue: number;
  active_members: number;
  avg_tenure_days: number;
  still_active_pct: number | null;
}
export function getPlanPerformance(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<PlanPerformanceRow[]> {
  return rows<PlanPerformanceRow>(
    c
      .from('v_plan_performance')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('revenue', { ascending: false }),
  );
}

// ── Retention risk ────────────────────────────────────────────────────────────
export interface AtRiskRow {
  member_id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  last_visit_at: string | null;
  visits_last_7_days: number;
  visits_this_month: number;
  days_since_last_visit: number | null;
  mrr_at_risk: number | null;
}
export function getMembersAtRisk(c: GymSupabaseClient, tenantId: string): Promise<AtRiskRow[]> {
  return rows<AtRiskRow>(
    c
      .from('v_members_at_risk')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('days_since_last_visit', {
        ascending: false,
        nullsFirst: true,
      }),
  );
}

export interface GhostRow {
  member_id: string;
  first_name: string;
  last_name: string;
  visits_this_month: number;
  last_visit_at: string | null;
  monthly_value: number | null;
}
export function getGhostMembers(c: GymSupabaseClient, tenantId: string): Promise<GhostRow[]> {
  return rows<GhostRow>(c.from('v_ghost_members').select('*').eq('tenant_id', tenantId));
}

// ── Acquisition ───────────────────────────────────────────────────────────────
export interface AcquisitionRow {
  source: string;
  members: number;
  active_members: number;
  active_pct: number | null;
}
export function getAcquisitionChannel(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<AcquisitionRow[]> {
  return rows<AcquisitionRow>(
    c
      .from('v_acquisition_channel')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('members', { ascending: false }),
  );
}

// ── Classes ───────────────────────────────────────────────────────────────────
export interface ClassPerfRow {
  session_id: string;
  class_name: string | null;
  instructor_staff_id: string | null;
  starts_at: string;
  is_pt: boolean;
  capacity: number;
  status: string;
  booked: number;
  attended: number;
  no_show: number;
  late_cancel: number;
  fill_rate_pct: number | null;
}
export function getClassPerformance(
  c: GymSupabaseClient,
  tenantId: string,
  range?: { from?: string; to?: string },
): Promise<ClassPerfRow[]> {
  let q = c.from('v_class_performance').select('*').eq('tenant_id', tenantId);
  if (range?.from) q = q.gte('starts_at', range.from);
  if (range?.to) q = q.lte('starts_at', range.to);
  return rows<ClassPerfRow>(q.order('starts_at', { ascending: false }));
}

export interface WaitlistDemandRow {
  class_session_id: string;
  class_name: string | null;
  starts_at: string;
  waitlist_total: number;
  waiting: number;
  promoted: number;
}
export function getWaitlistDemand(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<WaitlistDemandRow[]> {
  return rows<WaitlistDemandRow>(
    c
      .from('v_waitlist_demand')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('waitlist_total', { ascending: false }),
  );
}

export interface SessionPackRow {
  membership_id: string;
  member_id: string;
  plan_type: string;
  credits_total: number | null;
  credits_remaining: number | null;
  credits_used: number;
  pct_used: number | null;
}
export function getSessionPackBurndown(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<SessionPackRow[]> {
  return rows<SessionPackRow>(
    c
      .from('v_session_pack_burndown')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('pct_used', { ascending: false }),
  );
}

// ── Accounts receivable aging ─────────────────────────────────────────────────
export interface ArAging {
  bucket_0_7: number;
  bucket_8_30: number;
  bucket_30_plus: number;
  overdue_total: number;
}
export async function getArAging(c: GymSupabaseClient, tenantId: string): Promise<ArAging | null> {
  const { data, error } = await c
    .from('v_ar_aging')
    .select('bucket_0_7, bucket_8_30, bucket_30_plus, overdue_total')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data as unknown as ArAging) ?? null;
}

// ── Predictive churn risk (transparent rule-based score + reasons, P2 §3.4.2) ─
export interface ChurnRiskRow {
  member_id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  tenure_days: number;
  monthly_value: number | null;
  days_since_last_visit: number | null;
  visits_last_7_days: number;
  payment_failures: number;
  has_freeze: boolean;
  risk_score: number;
  reason_inactive: boolean;
  reason_no_recent_visits: boolean;
  reason_new_member: boolean;
  reason_frozen_before: boolean;
  reason_payment_failed: boolean;
}
export function getChurnRisk(c: GymSupabaseClient, tenantId: string): Promise<ChurnRiskRow[]> {
  return rows<ChurnRiskRow>(
    c
      .from('v_member_churn_risk')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('risk_score', { ascending: false }),
  );
}

// ── RFM segmentation (P2 §3.4.5) ──────────────────────────────────────────────
export interface RfmSegmentRow {
  segment: string;
  members: number;
  monetary_total: number;
}
export function getRfmSegments(c: GymSupabaseClient, tenantId: string): Promise<RfmSegmentRow[]> {
  return rows<RfmSegmentRow>(
    c
      .from('v_rfm_segments')
      .select('segment, members, monetary_total')
      .eq('tenant_id', tenantId)
      .order('members', { ascending: false }),
  );
}

// ── LTV by plan (units vs lifetime value, P2 §3.1/§3.3) ───────────────────────
export interface LtvByPlanRow {
  plan_id: string;
  plan_name: string;
  plan_type: string;
  units_sold: number;
  active_members: number;
  avg_monthly_value: number | null;
  avg_tenure_months: number | null;
  ltv: number | null;
}
export function getLtvByPlan(c: GymSupabaseClient, tenantId: string): Promise<LtvByPlanRow[]> {
  return rows<LtvByPlanRow>(
    c
      .from('v_ltv_by_plan')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('ltv', { ascending: false, nullsFirst: false }),
  );
}

// ── Retail attach rate (P1 §3.7) ──────────────────────────────────────────────
export interface RetailAttach {
  active_members: number;
  members_with_retail: number;
  attach_pct: number | null;
  retail_orders: number;
  avg_basket: number;
}
export async function getRetailAttach(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<RetailAttach | null> {
  const { data, error } = await c
    .from('v_retail_attach')
    .select('active_members, members_with_retail, attach_pct, retail_orders, avg_basket')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data as unknown as RetailAttach) ?? null;
}

// ── Business-health composite (P2 §4.6) ───────────────────────────────────────
export interface BusinessHealth {
  health_score: number | null;
  retention_score: number | null;
  engagement_score: number | null;
  growth_score: number | null;
  margin_score: number | null;
  active_members: number;
  visiting: number;
  churn_rate_pct: number | null;
  net_growth: number | null;
  income: number | null;
  profit: number | null;
}
export async function getBusinessHealth(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<BusinessHealth | null> {
  const { data, error } = await c
    .from('v_business_health')
    .select(
      'health_score, retention_score, engagement_score, growth_score, margin_score, active_members, visiting, churn_rate_pct, net_growth, income, profit',
    )
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data as unknown as BusinessHealth) ?? null;
}

// ── Renewal forecast (reused: existing v_renewal_forecast, BA-07) ─────────────
export interface RenewalForecastRow {
  plan_id: string;
  plan_type: string;
  plan_price: number;
  memberships_due: number;
  projected_revenue: number;
  renewal_month: string;
}
export function getRenewalForecast(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<RenewalForecastRow[]> {
  return rows<RenewalForecastRow>(
    c
      .from('v_renewal_forecast')
      .select('plan_id, plan_type, plan_price, memberships_due, projected_revenue, renewal_month')
      .eq('tenant_id', tenantId)
      .order('renewal_month'),
  );
}

// ── Retail product performance (best/worst sellers, §3.7) ─────────────────────
export interface ProductSalesRow {
  product_id: string;
  product_name: string;
  category: string | null;
  units_sold: number;
  revenue: number;
  cost_total: number | null;
  gross_margin: number | null;
  margin_pct: number | null;
}
export function getProductSales(
  c: GymSupabaseClient,
  tenantId: string,
): Promise<ProductSalesRow[]> {
  return rows<ProductSalesRow>(
    c
      .from('v_product_sales')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('units_sold', { ascending: false }),
  );
}
